import type { InboxItem } from '@elutide/shared';
import { AgeText } from './Provenance';
import { Button, Pill } from './Primitives';

export interface InboxRowProps {
  item: InboxItem;
  assigneeName?: string | null;
  onAssign?: (item: InboxItem) => void;
  onSnooze?: (item: InboxItem) => void;
  onResolve?: (item: InboxItem) => void;
  onOpenSource?: (item: InboxItem) => void;
  focused?: boolean;
}

/**
 * Severity bar, title, one muted context line, age, assignee, one contextual primary action
 * and an overflow for the rest. Colour is never the only signal: a crit row carries the word
 * as well as the bar.
 */
export function InboxRow({
  item,
  assigneeName,
  onAssign,
  onSnooze,
  onResolve,
  onOpenSource,
  focused = false,
}: InboxRowProps) {
  const severityClass =
    item.severity === 'crit'
      ? 'o-inbox__sev o-inbox__sev--crit'
      : item.severity === 'warn'
        ? 'o-inbox__sev o-inbox__sev--warn'
        : 'o-inbox__sev';

  return (
    <li
      className="o-inbox__row"
      data-focused={focused ? 'true' : undefined}
      // A crit arrival is announced; warn and info are not, or the room stops listening.
      role={item.severity === 'crit' ? 'alert' : 'listitem'}
    >
      <span className={severityClass} aria-hidden="true" />

      <div className="o-inbox__main">
        <div className="o-inbox__title">
          <Pill tone={item.severity === 'crit' ? 'crit' : item.severity === 'warn' ? 'warn' : 'info'}>
            {item.severity}
          </Pill>
          <span className="o-inbox__title-text">{item.title}</span>
        </div>
        <div className="o-inbox__context">
          {item.context ? `${item.context} · ` : ''}
          <AgeText at={item.created_at} /> · {assigneeName ?? 'Unassigned'}
          {item.state === 'snoozed' ? ' · snoozed' : ''}
        </div>
      </div>

      <div className="o-inbox__actions">
        {item.primary_action && onOpenSource ? (
          <Button size="sm" variant="primary" onClick={() => onOpenSource(item)}>
            {item.primary_action.label}
          </Button>
        ) : null}
        {onAssign ? (
          <Button size="sm" variant="ghost" onClick={() => onAssign(item)}>
            Assign
          </Button>
        ) : null}
        {onSnooze ? (
          <Button size="sm" variant="ghost" onClick={() => onSnooze(item)}>
            Snooze
          </Button>
        ) : null}
        {onResolve ? (
          <Button size="sm" variant="ghost" onClick={() => onResolve(item)}>
            Resolve
          </Button>
        ) : null}
      </div>
    </li>
  );
}
